/**
 * Typescript wrapper for simple object copy through JSON
 */
export function jsonCopyObject<T extends object>(object: T): T {
  return JSON.parse(JSON.stringify(object));
}

// It works much better than loadash.isEqual
export function isJSONEqual<T extends object>(object1: T, object2: T): boolean {
  return JSON.stringify(object1) === JSON.stringify(object2);
}

/**
 * Simple object check.
 * @param item The value to check if it is a plain object.
 * @returns {boolean}
 */
export function isObject(item: unknown): item is Record<string, unknown> {
  return (item !== null && typeof item === 'object' && !Array.isArray(item));
}

/**
 * This function solves the TS issue where Object.entries() looses the type of the key if enums are used as keys.
 * @param obj
 */
export function objectEntries<K extends string | symbol, V>(obj: Record<K, V>): [K, V][] {
  return Object.entries(obj) as [K, V][];
}

/**
 * Typed wrapper for Object.entries() that preserves numeric keys.
 * Object.entries() always returns keys as strings; this function converts them back to numbers.
 * @param obj An object whose keys are numbers.
 */
export function objectNumericEntries<K extends number, V>(obj: Record<K, V>): [K, V][] {
  return Object.entries(obj).map(([
    key,
    value,
  ]) => [
    Number(key),
    value,
  ]) as [K, V][];
}

/**
 * This function solves the TS issue where Object.keys() looses the type of the key if enums are used as keys.
 * @param obj
 */
export function objectKeys<K extends string | symbol>(obj: Record<K, any>): K[] {
  return Object.keys(obj) as K[];
}

/**
 * Typed wrapper for Object.keys() that preserves numeric keys.
 * Object.keys() always returns keys as strings; this function converts them back to numbers.
 * @param obj An object whose keys are numbers.
 */
export function objectNumericKeys<K extends number>(obj: Record<K, any>): K[] {
  return Object.keys(obj).map((key) => Number(key)) as K[];
}

/**
 * Whether the value is an object literal, as opposed to arrays, dates, maps or class instances.
 * Also true for a Vue reactive proxy of an object literal.
 */
export function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Object.prototype.toString.call(value) === '[object Object]';
}

/**
 * Deep copy of JSON-like data: object literals, arrays and primitives. Other objects, such as
 * dates, are kept by reference. Unlike `structuredClone`, it also copies Vue reactive proxies.
 */
export function clonePlainData<T>(data: T): T {
  if (Array.isArray(data)) {
    return data.map((item) => clonePlainData(item)) as T;
  }
  if (isPlainObject(data)) {
    return Object.fromEntries(Object.entries(data).map(([
      key,
      propertyValue,
    ]) => [
      key,
      clonePlainData(propertyValue),
    ])) as T;
  }
  return data;
}

/**
 * Deep equality of JSON-like data: object literals, arrays and primitives. A property that is
 * `undefined` equals a missing property. Other objects are compared by reference.
 */
export function isPlainDataEqual(first: unknown, second: unknown): boolean {
  if (first === second || (Number.isNaN(first) && Number.isNaN(second))) {
    return true;
  }
  if (Array.isArray(first) && Array.isArray(second)) {
    return first.length === second.length
      && first.every((item, index) => isPlainDataEqual(item, second[index]));
  }
  if (isPlainObject(first) && isPlainObject(second)) {
    const keys = new Set([
      ...Object.keys(first),
      ...Object.keys(second),
    ]);
    return [
      ...keys,
    ].every((key) => isPlainDataEqual(first[key], second[key]));
  }
  return false;
}
